import React, { memo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton, SvgIcon, Typography } from '@mui/material';
import { truncate } from '@provider-portal/common/utils/strings';

import { selectMenuDishesEntities, selectMenuOptionsEntities } from 'rdx/menu';
import { getLocalisedStringValue } from 'utils/locales';

import AvailabilityField from './fields/AvailabilityField';
import MenuItemInfo from './MenuItemInfo';

interface MenuItemProps {
  id: number;
}

const MenuItem: React.FC<MenuItemProps> = ({ id }) => {
  const dishes = useSelector(selectMenuDishesEntities);
  const options = useSelector(selectMenuOptionsEntities);
  const [isExpanded, setExpandState] = useState(false);

  const item = dishes[id] || options[id];

  const handleToggleExpand = useCallback(() => setExpandState((prevState) => !prevState), []);

  if (!item) {
    return null;
  }

  return (
    <Box
      px={2}
      py={1}
      sx={{
        borderBottom: 'solid 1px',
        borderColor: 'colors.neutral400',
        ...(isExpanded && { backgroundColor: 'colors.neutral100' }),
      }}
    >
      <Box
        display="flex"
        alignItems={item.description ? 'center' : 'baseline'}
        style={{ height: 'fit-content' }}
        sx={{ height: '64px', overflow: 'hidden' }}
      >
        <Box mr={2} mt={2}>
          <IconButton color="inherit" onClick={handleToggleExpand} size="small">
            <SvgIcon>{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</SvgIcon>
          </IconButton>
        </Box>
        <Box mr={2} flexGrow={2} flex={1}>
          <Box>
            <Typography variant="subtitleM">{getLocalisedStringValue(item.name)}</Typography>
          </Box>
          <Box>
            <Typography variant="bodyS">
              {truncate(getLocalisedStringValue(item.description), 200, true)}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" mt={2} flex={0.25}>
          <Box>
            <AvailabilityField showLabel={false} id={id} />
          </Box>
          <Box ml={2}>
            <Typography variant="bodyM">{item.price?.price_str}</Typography>
          </Box>
        </Box>
      </Box>
      <Collapse in={isExpanded}>
        <Box m={2}>
          <MenuItemInfo item={item} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default memo(MenuItem);
